import axios from '../../api/axiosInstance';

import { processRelationshipNetworkMemory } from '../relationships/processRelationshipNetworkMemory';
import { shortReplyTriggers, shortReplies } from '../shaping/shortResponses';
import buildContextEnvelope from '../context/buildContextEnvelope';
import resolveResponseShapingPolicy from '../shaping/resolveResponseShapingPolicy';
import { detectRelationshipDeclaration } from '../intents/relationshipIntentUtils';
import buildContext from '../contextBuilder';
import buildConversationState from '../conversation/buildConversationState';
import { prepareMemoryPayload } from '../memory/prepareMemoryPayload';
import { fetchWebSearchResults } from '../../hooks/webSearch';
import { getPendingSelectionSignals } from '../conversation/pendingSelectionSignals';
import { shouldUseVectorMemory } from '../conversation/turnSignalEligibility';
import { buildPendingQuestionReply } from '../conversation/pendingQuestionResponses';
import {
  normalizeRelationshipRole,
  detectGroupedRelationshipRecall,
  getRelationshipsByGroup,
  buildLastReferencedEntity,
  formatNaturalNameList,
} from '../relationships/relationshipRecall';
import {
  shouldResetEphemeralState,
  getActiveReferencedEntity,
  getActiveReferencedTopic,
  buildPromptMemoryState,
} from '../conversation/promptMemoryState';
import generateGovernedResponse from './generateGovernedResponse';
import { saveVectorMemory, queryVectorMemory } from '../../api/memory';
import buildTurnInterpretation from '../conversation/buildTurnInterpretation';
import buildIntentState from '../conversation/buildIntentState';
import { upsertRelationshipState } from '../memory/mongoMemoryUtils';
import { validateExecution } from '../execution/executionValidator';
import { processPendingExecution } from '../execution/processPendingExecution';
import { debugLog } from '../logging/debugLog';

let lastTarget = null;

function shouldTriggerFollowup(message, lastIntent, lastSearchTopic) {
  const lower = message.toLowerCase().trim();
  const followupTriggers = [
    'yes',
    'tell me more',
    'what happened',
    'more',
    'what else',
    'continue',
    'go on',
    'explain more',
  ];

  const isShort = lower.split(/\s+/).length <= 5;
  const containsTrigger = followupTriggers.some((trigger) => lower.includes(trigger));

  const isLikelyFollowup = isShort && containsTrigger && !!lastSearchTopic;

  const overrideIntents = ['trivia_question', 'real_time_info', 'current_event'];
  const isNewTopicIntent = overrideIntents.includes(lastIntent);

  return isLikelyFollowup && !isNewTopicIntent;
}

export default async function processConversationTurn({
  message,
  memory,
  relationships,
  relationshipNetwork,
  messages,
  activeIdentity,
  effectiveUserName,
  user,
  updateMemory,
  patchRelationshipStates,
  patchRelationshipNetworkEdges,
  setLastExecutionDecision,
  setLastResponsePolicyResult,
  setDebugConversationState,
  setMessages,
  setLoading,
  appendMessagePair,
  commitPendingQuestionFromReply,
  commitReplyWithPendingQuestion,
}) {
  setLastResponsePolicyResult(null);
  let currentRelationships = relationships;
  let currentRelationshipNetwork = relationshipNetwork;

  const pendingExecutionResult = await processPendingExecution({
    message,
    memory,
    currentRelationships,
    currentRelationshipNetwork,
    effectiveUserName,
    updateMemory,
    setLastExecutionDecision,
    appendMessagePair,
    patchRelationshipStates,
    patchRelationshipNetworkEdges,
  });

  message = pendingExecutionResult.message;
  currentRelationships = pendingExecutionResult.currentRelationships;
  currentRelationshipNetwork =
    pendingExecutionResult.currentRelationshipNetwork || currentRelationshipNetwork;

  if (pendingExecutionResult.handled) {
    return;
  }

  const lowerCaseInput = message.toLowerCase();

  // 🚫 FAVORITES DISABLED (incomplete lane)
  // const favoriteFlowResult = handleFavoriteFlow({
  //   message,
  //   memory,
  //   pendingFavorite,
  //   setPendingFavorite,
  //   commitReplyWithPendingQuestion,
  // });

  // if (favoriteFlowResult?.handled) {
  //   return;
  // }

  const explicitRelationshipDeclaration = await detectRelationshipDeclaration(
    message,
    currentRelationships
  );

  const relationshipDeclarationForAdmission =
    explicitRelationshipDeclaration?.alreadyKnown === true
      ? null
      : explicitRelationshipDeclaration;

  const conversationState = buildConversationState({
    message,
    memory,
    relationships: currentRelationships,
    relationshipNetwork: currentRelationshipNetwork,
    messages,
    activeIdentity,
    pendingQuestion: memory?.pendingQuestion || null,
    pendingSelectionSignals: getPendingSelectionSignals(message),
    explicitRelationshipDeclaration: relationshipDeclarationForAdmission,
  });

  setDebugConversationState(conversationState);

  const relationshipCorrectionCandidate =
    conversationState.relationshipCorrectionCandidate || null;

  if (relationshipCorrectionCandidate) {
    debugLog(
      '🧠 [Chat] relationshipCorrectionCandidate detected:',
      relationshipCorrectionCandidate
    );

    const correctionTarget = {
      type: 'relationship',
      entityId: relationshipCorrectionCandidate.entity?.entityId || null,
      name: relationshipCorrectionCandidate.entity?.name || null,
      role: relationshipCorrectionCandidate.relationship?.role || null,
    };

    if (correctionTarget.name && correctionTarget.role) {
      updateMemory('pendingCorrection', {
        ...correctionTarget,
        createdAt: new Date().toISOString(),
      });
      updateMemory('pendingQuestion', null);

      setLastExecutionDecision({
        decision: 'CONFIRM',
        displayDecision: 'CONFIRM',
        intent: 'relationship_remove',
        category: 'memory',
        operation: 'delete',
        target: correctionTarget.name,
        reason: 'Relationship removal requires explicit confirmation before memory can change.',
      });

      appendMessagePair(
        message,
        `Just to confirm — should I remove ${correctionTarget.name} as your ${correctionTarget.role}?`
      );
      return;
    }
  }

  const relationshipStateCandidate =
    conversationState.relationshipStateCandidate || null;

  if (relationshipStateCandidate) {
    debugLog(
      '🧠 [Chat] relationshipStateCandidate detected:',
      relationshipStateCandidate
    );

    const relationshipTransitionType = relationshipStateCandidate.field
      ? 'relationship_update'
      : 'relationship_add';

    const executionCheck = validateExecution({
      intent: { type: relationshipTransitionType },
      targetEntity: {
        name: relationshipStateCandidate.first_name || null,
      },
      relationshipState: currentRelationships,
      message,
      conversationState,
    });

    setLastExecutionDecision({
      ...executionCheck,
      displayDecision: executionCheck.decision,
    });

    if (executionCheck.decision === 'BLOCK') {
      appendMessagePair(
        message,
        "I’m not able to make that relationship change right now."
      );
      return;
    }

    if (executionCheck.decision === 'CONFIRM') {
      appendMessagePair(
        message,
        `Just to confirm — should I update ${relationshipStateCandidate.first_name}?`
      );
      return;
    }

    try {
      const upsertPayload = {
        entityId: relationshipStateCandidate.entityId || null,
        first_name: relationshipStateCandidate.first_name,
        last_name: relationshipStateCandidate.last_name || null,
        relationship: relationshipStateCandidate.relationship,
        source: 'conversation',
      };

      if (relationshipStateCandidate.field === 'lifeState') {
        upsertPayload.lifeState = relationshipStateCandidate.value;
      }

      if (relationshipStateCandidate.field === 'healthState') {
        upsertPayload.healthState = relationshipStateCandidate.value;
      }

      if (relationshipStateCandidate.field === 'location') {
        upsertPayload.location = relationshipStateCandidate.value;
      }

      const upsertedRelationship = await upsertRelationshipState(upsertPayload);

      debugLog(
        '✅ [Chat] relationship state upserted:',
        upsertedRelationship
      );

      if (!upsertedRelationship) {
        throw new Error('Relationship state upsert returned no canonical record');
      }

      currentRelationships = typeof patchRelationshipStates === 'function'
        ? patchRelationshipStates(upsertedRelationship)
        : currentRelationships;

      debugLog(
        '🧑‍🤝‍🧑 [Chat] Relationship state patched locally after upsert:',
        currentRelationships
      );

      setLastExecutionDecision({
        ...executionCheck,
        displayDecision: 'COMPLETED',
        reason: `Relationship state update completed for ${relationshipStateCandidate.first_name}.`,
      });
    } catch (err) {
      console.error(
        '❌ [Chat] Failed relationship state upsert/refresh:',
        err.message
      );

      setLastExecutionDecision({
        decision: 'BLOCK',
        displayDecision: 'BLOCK',
        intent: relationshipTransitionType,
        category: 'memory',
        operation: 'write',
        target: relationshipStateCandidate.first_name || '—',
        reason: `Relationship state update failed for ${relationshipStateCandidate.first_name}.`,
      });
    }
  }

  const shouldResetEphemeral = shouldResetEphemeralState(conversationState);
  const entityState = conversationState.entityState || null;
  const entityStateActive = entityState?.active || null;

  if (shouldResetEphemeral) {
    lastTarget = null;
  }

  debugLog('🧠 [PendingDebug] conversationState:', {
    pendingQuestionState: conversationState.pendingQuestionState,
    detectedAnswerToPendingQuestion:
      conversationState.pendingQuestionState?.detectedAnswerToPendingQuestion,
    pendingQuestionText: conversationState.pendingQuestionState?.pendingQuestion?.text || null,
    nextPendingQuestionText:
      conversationState.pendingQuestionState?.nextPendingQuestion?.text || null,
    lifecycle: conversationState.lifecycle || null,
    entityState: conversationState.entityState || null,
    objectiveDecisionState: conversationState.objectiveDecisionState || null,
    relationshipStateCandidate: conversationState.relationshipStateCandidate || null,
    relationshipCorrectionCandidate: conversationState.relationshipCorrectionCandidate || null,
  });

  debugLog('🧠 [PendingDebug] memory.pendingQuestion:', memory.pendingQuestion);
  debugLog('🧪 Pending question state:', conversationState.pendingQuestionState);

  updateMemory('frame', conversationState.frame);
  updateMemory('lastReferencedTopic', conversationState.reference.lastReferencedTopic || null);
  updateMemory('activeObjective', conversationState.objectiveDecisionState?.activeObjective || null);
  updateMemory('activeDecision', conversationState.objectiveDecisionState?.activeDecision || null);
  updateMemory('activeReasoningChain', conversationState.objectiveDecisionState?.activeReasoningChain || []);

  if (conversationState.reference.lastReferencedEntity) {
    updateMemory('lastReferencedEntity', conversationState.reference.lastReferencedEntity);
  } else if (entityStateActive) {
    updateMemory('lastReferencedEntity', entityStateActive);
  } else if (shouldResetEphemeral) {
    updateMemory('lastReferencedEntity', null);
  }

  if (shouldResetEphemeral) {
    updateMemory('pendingQuestion', null);
    updateMemory('lastSearchTopic', null);
    updateMemory('activeObjective', null);
    updateMemory('activeDecision', null);
    updateMemory('activeReasoningChain', []);
    updateMemory('intentState', null);
    updateMemory('activeUserIntent', null);
    updateMemory('turnFunction', null);
    updateMemory('responseIntent', null);
  }

  const activeReferencedEntity = getActiveReferencedEntity({
    conversationState,
    memoryState: memory,
  });

  const activeReferencedTopic = getActiveReferencedTopic({
    conversationState,
    memoryState: memory,
  });

  const pendingQuestionAnswered =
    conversationState.pendingQuestionState?.detectedAnswerToPendingQuestion === true;

  const pendingQuestionText =
    conversationState.pendingQuestionState?.pendingQuestion?.text?.toLowerCase() || '';

  const hasPendingQuestion =
    !!conversationState.pendingQuestionState?.pendingQuestion;

  const selectionPromptActive = [
    'someone specific',
    'one in particular',
    'which one',
    'which person',
    'who were you thinking about',
    'who did you mean',
  ].some((phrase) => pendingQuestionText.includes(phrase));

  const {
    greetingLike,
    closureLike,
    noSpecificSelectionLike,
    extractedCandidate,
  } = getPendingSelectionSignals(message);

  const arbitration = conversationState.turnSignalArbitration || null;

  debugLog('🧠 [Arbitration]', arbitration);

  const relationshipGroup = detectGroupedRelationshipRecall(message);

  if (relationshipGroup) {
    updateMemory('pendingQuestion', null);

    setLastExecutionDecision({
      decision: 'IDLE',
      displayDecision: 'IDLE',
      intent: 'relationship_recall',
      category: 'read',
      operation: 'none',
      target: relationshipGroup,
      reason: 'Relationship recall is read-only. No execution mutation requested.',
    });

    const matchedPeople = getRelationshipsByGroup(relationshipGroup, currentRelationships);

    let botReply = '';

    if (!currentRelationships?.length) {
      botReply = `I don’t have any saved relationship details yet, ${effectiveUserName}.`;
    } else if (!matchedPeople.length) {
      if (relationshipGroup === 'all') {
        botReply =
          'I remember some people in your life, but I don’t have them organized cleanly enough yet to answer that well.';
      } else {
        botReply = `I don’t have any saved ${relationshipGroup}s yet, ${effectiveUserName}.`;
      }
    } else if (matchedPeople.length === 1) {
      const onlyPerson = matchedPeople[0];
      const singleReferencedEntity = buildLastReferencedEntity({
        name: onlyPerson.first_name,
        role: onlyPerson.relationship,
        entityId: onlyPerson.entityId || null,
      });

      updateMemory('lastReferencedEntity', singleReferencedEntity);

      botReply = `Yes — I remember ${onlyPerson.first_name} as your ${onlyPerson.relationship}.`;

      commitReplyWithPendingQuestion({
        userMessage: message,
        botReply,
        frame: conversationState.frame || memory?.frame || null,
        lastReferencedEntity: singleReferencedEntity,
        lastReferencedTopic:
          activeReferencedTopic || {
            topic: 'relationships',
            matchedBy: relationshipGroup,
            source: 'group_recall_reply',
          },
      });

      return;
    } else {
      const naturalList = formatNaturalNameList(matchedPeople);
      botReply = `I remember several ${relationshipGroup}s — ${naturalList}. Is there someone specific you were thinking about?`;

      updateMemory('lastReferencedEntity', null);

      commitReplyWithPendingQuestion({
        userMessage: message,
        botReply,
        frame: conversationState.frame || memory?.frame || null,
        lastReferencedEntity: null,
        lastReferencedTopic:
          activeReferencedTopic || {
            topic: 'relationships',
            matchedBy: relationshipGroup,
            source: 'group_recall_reply',
          },
      });

      return;
    }

    commitReplyWithPendingQuestion({
      userMessage: message,
      botReply,
      frame: conversationState.frame || memory?.frame || null,
      lastReferencedEntity: activeReferencedEntity,
      lastReferencedTopic:
        activeReferencedTopic || {
          topic: 'relationships',
          matchedBy: relationshipGroup,
          source: 'group_recall_reply',
        },
    });

    return;
  }

  const noResolvedEntity =
    !entityStateActive && !conversationState.reference.lastReferencedEntity;

  const shouldHandleNoSpecificSelection =
    hasPendingQuestion &&
    selectionPromptActive &&
    noSpecificSelectionLike &&
    !arbitration?.control?.suppressNoSpecificSelectionReply &&
    !explicitRelationshipDeclaration &&
    !conversationState.pendingQuestionState?.pendingAuthorityRevoked;

  const shouldHandleUnknownSelection =
    hasPendingQuestion &&
    selectionPromptActive &&
    !!extractedCandidate &&
    !greetingLike &&
    !closureLike &&
    !noSpecificSelectionLike &&
    !explicitRelationshipDeclaration &&
    noResolvedEntity;

  if (
    !explicitRelationshipDeclaration &&
    (
      (
        pendingQuestionAnswered &&
        !conversationState.pendingQuestionState?.pendingAuthorityRevoked
      ) ||
      shouldHandleUnknownSelection ||
      shouldHandleNoSpecificSelection
    )
  ) {
    updateMemory('pendingQuestion', null);

    if (shouldHandleNoSpecificSelection) {
      const botReply =
        "That's okay. I was just checking in. We can leave it there, or talk about something else.";

      commitReplyWithPendingQuestion({
        userMessage: message,
        botReply,
        frame: conversationState.frame || memory?.frame || null,
        lastReferencedEntity: null,
        lastReferencedTopic: activeReferencedTopic,
      });

      return;
    }

    if (shouldHandleUnknownSelection) {
      const botReply = `Got it — ${extractedCandidate}. I don’t believe I have a memory of ${extractedCandidate} yet. Want to tell me a little more about that?`;

      commitReplyWithPendingQuestion({
        userMessage: message,
        botReply,
        frame: conversationState.frame || memory?.frame || null,
        lastReferencedEntity: null,
        lastReferencedTopic:
          activeReferencedTopic ||
          { topic: 'unknown_selection', source: 'pending_question_resolution' },
      });

      return;
    }

    const shouldAutoReplyLocally =
      conversationState.pendingQuestionState?.shouldAutoReplyLocally === true;

    if (shouldAutoReplyLocally) {
      const botReply = buildPendingQuestionReply(
        conversationState.pendingQuestionState,
        message
      );

      const clarificationLikeMessages = [
        'i mean',
        'i meant',
        'i just meant',
        'no i mean',
        'no i meant',
        'more friends',
        'other friends',
        'i have more friends',
        'you only know',
        'not thinking about one',
      ];

      const isClarificationReply = clarificationLikeMessages.some((phrase) =>
        String(message).toLowerCase().includes(phrase)
      );

      if (isClarificationReply) {
        updateMemory('lastReferencedEntity', null);
      }

      commitReplyWithPendingQuestion({
        userMessage: message,
        botReply,
        frame: conversationState.frame || memory?.frame || null,
        lastReferencedEntity: isClarificationReply
          ? null
          : activeReferencedEntity,
        lastReferencedTopic: isClarificationReply
          ? activeReferencedTopic || {
              topic: 'relationships',
              matchedBy: 'friend',
              source: 'clarification_reply',
            }
          : activeReferencedTopic,
      });

      return;
    }
  }

  if (
    !pendingQuestionAnswered &&
    !shouldHandleUnknownSelection &&
    !shouldHandleNoSpecificSelection &&
    conversationState.control?.requiresConfirmation
  ) {
    const botReply =
      'I see where you’re going. Before I shift away from our current plan, do you want to make this the new priority?';

    commitReplyWithPendingQuestion({
      userMessage: message,
      botReply,
      frame: conversationState.frame || memory?.frame || null,
      lastReferencedEntity: activeReferencedEntity,
      lastReferencedTopic: activeReferencedTopic,
    });

    return;
  }

  const pronounResolutionSignal = conversationState.pronounResolutionSignal || null;

  const attachedEntity =
    pronounResolutionSignal?.resolved && pronounResolutionSignal?.candidateEntity
      ? pronounResolutionSignal.candidateEntity
      : entityStateActive || activeReferencedEntity;

  const attachedTopic = activeReferencedTopic;

  const turnInterpretation = await buildTurnInterpretation({
    message,
    conversationState,
    lastTarget,
    relationships: currentRelationships,
    fallbackEntity: attachedEntity,
    fallbackTopic: attachedTopic,
    explicitRelationshipDeclaration,
  });

  const intentInfo = turnInterpretation.turnIntentCandidate;

  if (
    intentInfo?.requiresClarification === true &&
    intentInfo?.writeMode === 'clarify_before_write'
  ) {
    updateMemory('pendingWriteConfirmation', null);
    updateMemory('pendingQuestion', null);

    setLastExecutionDecision({
      decision: 'BLOCK',
      displayDecision: 'BLOCK',
      intent: 'relationship_add',
      category: 'memory',
      operation: 'write',
      target: intentInfo.personName || '—',
      reason: 'First-order relationship identity is ambiguous; no mutation allowed.',
    });

    appendMessagePair(
      message,
      `I know more than one ${intentInfo.personName}. Which one do you mean?`
    );
    return;
  }

  if (intentInfo?.alreadyKnown === true) {
    debugLog('🧭 [FirstOrderRelationship] Existing relationship recognized; confirmation/write suppressed:', {
      personName: intentInfo.personName || null,
      relationship: intentInfo.relationship || null,
      entityId: intentInfo.entityId || null,
    });
  }

  const intentState = buildIntentState({
    message,
    currentIntentState: memory?.intentState || null,
    turnInterpretation,
    objectiveDecisionState: conversationState.objectiveDecisionState || null,
    turnSignalArbitration: arbitration,
    shouldResetEphemeral,
  });

  conversationState.turnInterpretation = turnInterpretation;
  conversationState.intentState = intentState;

  const shapingPolicy = resolveResponseShapingPolicy({
    intent: intentInfo?.intent || 'unknown',
    frame: conversationState.frame || memory?.frame || null,
    intentState,
    responseIntent: intentState?.responseIntent || null,
    objectiveDecisionState: conversationState.objectiveDecisionState || null,
    turnSignalArbitration: arbitration,
    pendingQuestionState: conversationState.pendingQuestionState || null,
    admissibilityState: conversationState.admissibilityState || null,
    turnInterpretation,
  });

  conversationState.shapingPolicy = shapingPolicy;

  updateMemory('intentState', intentState);
  updateMemory('activeUserIntent', intentState.activeUserIntent || null);
  updateMemory('turnFunction', intentState.turnFunction || null);
  updateMemory('responseIntent', intentState.responseIntent || null);

  setDebugConversationState({
    ...conversationState,
    turnInterpretation,
    intentState,
  });

  debugLog('🧭 [IntentState]', intentState);

  if (intentInfo?.requiresConfirmation && intentInfo?.writeMode === 'confirm_before_write') {
    updateMemory('pendingWriteConfirmation', {
      type: 'relationship',
      originalMessage: message,
      intentInfo,
      relationship: intentInfo.relationship,
      personName: intentInfo.personName,
      createdAt: new Date().toISOString(),
    });
    updateMemory('pendingQuestion', null);

    setLastExecutionDecision({
      decision: 'CONFIRM',
      displayDecision: 'CONFIRM',
      intent: 'relationship_add',
      category: 'memory',
      operation: 'write',
      target: intentInfo.personName || '—',
      reason: 'Relationship write requires explicit confirmation before memory can change.',
    });

    appendMessagePair(
      message,
      `Just to confirm — should I remember ${intentInfo.personName} as your ${intentInfo.relationship}?`
    );
    return;
  }

  const isFollowup = shouldTriggerFollowup(message, intentInfo.intent, memory.lastSearchTopic);

  if (isFollowup) {
    debugLog('🔄 User requested more info about:', memory.lastSearchTopic);
    const botReply = `I can definitely share more about ${memory.lastSearchTopic}. Would you like a quick update? 📚`;

    commitReplyWithPendingQuestion({
      userMessage: message,
      botReply,
      frame: conversationState.frame || memory?.frame || null,
      lastReferencedEntity: attachedEntity,
      lastReferencedTopic: attachedTopic,
    });

    return;
  }

  const memoryName = intentInfo.memoryName || null;

  if (intentInfo.target) {
    lastTarget = intentInfo.target;
  }

  const roleMatchCount = intentInfo.target
    ? currentRelationships.filter((person) => {
        const role = normalizeRelationshipRole(
          person?.relationship || person?.relationship_type
        );
        return role === normalizeRelationshipRole(intentInfo.target);
      }).length
    : 0;

  const normalizedTargetRole = normalizeRelationshipRole(intentInfo.target);
  const explicitTargetEntities = [
    entityStateActive,
    ...(Array.isArray(entityState?.named) ? entityState.named : []),
  ].filter(
    (entity) =>
      entity?.signals?.explicitlyNamed === true &&
      entity?.type !== 'relationship_group' &&
      normalizeRelationshipRole(entity?.role) === normalizedTargetRole
  );

  const uniqueExplicitTargetEntities = [];
  const explicitTargetKeys = new Set();

  explicitTargetEntities.forEach((entity) => {
    const identityKey = String(
      entity?.key ||
        entity?.entityId ||
        `${String(entity?.name || '').trim().toLowerCase()}::${normalizedTargetRole}`
    ).trim();

    if (!identityKey || explicitTargetKeys.has(identityKey)) return;

    explicitTargetKeys.add(identityKey);
    uniqueExplicitTargetEntities.push(entity);
  });

  const explicitSingularTargetEntity =
    uniqueExplicitTargetEntities.length === 1
      ? uniqueExplicitTargetEntities[0]
      : null;

  const isPluralRelationshipRecall =
    ['recall_request', 'memory_recall'].includes(intentInfo.intent) &&
    !!intentInfo.target &&
    !memoryName &&
    roleMatchCount > 1 &&
    !explicitSingularTargetEntity;

  const shouldPreservePronounContinuity =
    arbitration?.control?.shouldPreservePronounContinuity === true;

  const turnOperations =
    conversationState?.activeFrameState?.situation?.turnOperations || null;
  const activeRequestTarget = turnOperations?.activeRequestTarget || null;
  const hasUnresolvedRelationshipRequest = Boolean(
    turnOperations?.requestedRole &&
      !activeRequestTarget &&
      !explicitSingularTargetEntity
  );

  let effectiveReferencedEntity =
    activeRequestTarget ||
    explicitSingularTargetEntity ||
    (hasUnresolvedRelationshipRequest ? null : entityStateActive || attachedEntity);
  let effectiveReferencedTopic = attachedTopic;

  if (isPluralRelationshipRecall && !shouldPreservePronounContinuity) {
    debugLog(
      `🧩 Clearing singular lastReferencedEntity for plural ${intentInfo.target} recall`
    );

    effectiveReferencedEntity = null;
    effectiveReferencedTopic = effectiveReferencedTopic || {
      topic: 'relationships',
      matchedBy: intentInfo.target,
      source: 'plural_relationship_recall',
    };

    updateMemory('lastReferencedEntity', null);
    updateMemory('lastReferencedTopic', effectiveReferencedTopic);
  } else if (memoryName && intentInfo.target) {
    const normalizedMemoryName = String(memoryName || '').trim().toLowerCase();
    const normalizedTargetRole = normalizeRelationshipRole(intentInfo.target);
    const matchingReferencedPeople = (Array.isArray(currentRelationships)
      ? currentRelationships
      : []
    ).filter((person) => {
      const personName = [person?.first_name, person?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
        .toLowerCase();

      return (
        personName === normalizedMemoryName &&
        normalizeRelationshipRole(person?.relationship) === normalizedTargetRole
      );
    });

    const uniqueEntityIds = [
      ...new Set(
        matchingReferencedPeople
          .map((person) => String(person?.entityId || '').trim())
          .filter(Boolean)
      ),
    ];

    effectiveReferencedEntity = buildLastReferencedEntity({
      name: memoryName,
      role: intentInfo.target,
      entityId: uniqueEntityIds.length === 1 ? uniqueEntityIds[0] : null,
    });

    updateMemory('lastReferencedEntity', effectiveReferencedEntity);
  } else if (explicitSingularTargetEntity) {
    effectiveReferencedEntity = explicitSingularTargetEntity;
    updateMemory('lastReferencedEntity', effectiveReferencedEntity);
  } else if (shouldPreservePronounContinuity && pronounResolutionSignal?.candidateEntity) {
    effectiveReferencedEntity = pronounResolutionSignal.candidateEntity;
    updateMemory('lastReferencedEntity', effectiveReferencedEntity);
  } else if (hasUnresolvedRelationshipRequest) {
    effectiveReferencedEntity = null;
    effectiveReferencedTopic = effectiveReferencedTopic || {
      topic: 'relationships',
      matchedBy: turnOperations.requestedRole,
      source: 'unresolved_relationship_request',
    };
  } else if (entityStateActive) {
    effectiveReferencedEntity = entityStateActive;
    updateMemory('lastReferencedEntity', effectiveReferencedEntity);
  }

  debugLog('🎯 Detected intent:', intentInfo);

  const executionCheck = validateExecution({
    intent: { type: intentInfo?.intent },
    targetEntity: effectiveReferencedEntity,
    relationshipState: currentRelationships,
    message,
    conversationState,
  });

  setLastExecutionDecision({
    ...executionCheck,
    displayDecision: executionCheck.decision,
  });

  const SEARCHABLE_INTENTS = new Set([
    'trivia_question',
    'real_time_info',
    'current_event',
    'topic_research',
  ]);

  const relationshipReferenceState = conversationState?.relationshipReferenceState || null;
  const currentMessageHasKnownEntityAnchor = Array.isArray(relationshipReferenceState?.anchors) &&
    relationshipReferenceState.anchors.some((anchor) =>
      Array.isArray(anchor?.sources) && anchor.sources.includes('current_message') &&
      Boolean(anchor?.entity?.entityId)
    );
  const relationshipBearingKnownEntityLookup = Boolean(
    Number(relationshipReferenceState?.evidence?.relationshipMentionCount || 0) > 0 &&
    Array.isArray(relationshipReferenceState?.anchors) &&
    relationshipReferenceState.anchors.some((anchor) => Boolean(anchor?.entity?.entityId))
  );
  const privateEntityLookup =
    currentMessageHasKnownEntityAnchor || relationshipBearingKnownEntityLookup;

  // A generic "who/what is" classifier must not grant the public web authority
  // over a known private entity. Real-time/current/research intents remain able
  // to search; only trivia-style lookup is subordinated to Daisy's relational world.
  const searchNeeded =
    SEARCHABLE_INTENTS.has(intentInfo.intent) &&
    !(intentInfo.intent === 'trivia_question' && privateEntityLookup);

  if (intentInfo.intent === 'trivia_question' && privateEntityLookup) {
    debugLog(
      '🛡️ [RetrievalBoundary] Web search suppressed for known private relational entity.'
    );
  }

  let webSearchResult = null;

  if (searchNeeded) {
    try {
      webSearchResult = await fetchWebSearchResults(message);
      debugLog('📡 Web Search result fetched:', webSearchResult);
    } catch (err) {
      console.error('❌ Web search failed:', err.message);
    }
  }

  if (webSearchResult) {
    updateMemory('lastSearchTopic', message);
    debugLog('📚 Stored lastSearchTopic:', message);
  }

  const matchedTrigger = shortReplyTriggers.find((trigger) => lowerCaseInput.includes(trigger));
  if (matchedTrigger) {
    const matchedReplies = shortReplies[matchedTrigger];
    const botReply = Array.isArray(matchedReplies)
      ? matchedReplies[Math.floor(Math.random() * matchedReplies.length)]
      : matchedReplies;

    const userName = effectiveUserName.replace(/^[a-z]/, (c) => c.toUpperCase());
    const finalReply = botReply.replace('{name}', userName);

    commitReplyWithPendingQuestion({
      userMessage: message,
      botReply: finalReply,
      frame: conversationState.frame || memory?.frame || null,
      lastReferencedEntity: effectiveReferencedEntity,
      lastReferencedTopic: effectiveReferencedTopic,
    });

    return;
  }

  const context = buildContext(message, '', memory.mongoMemories || []);

  const isAttachedShortTurn =
    intentInfo?.source === 'turn_attachment' &&
    (message || '').trim().split(/\s+/).length <= 8;

  const useVectorMemory = isAttachedShortTurn
    ? false
    : shouldUseVectorMemory({
        message,
        intent: intentInfo.intent,
        context,
        matchedTrigger: !!matchedTrigger,
        detectedAnswerToPendingQuestion: pendingQuestionAnswered,
      });

  const token = user?.token;

  let recalledMemories = [];
  if (useVectorMemory) {
    try {
      const matches = await queryVectorMemory({ text: message, token });
      recalledMemories = matches
        .filter(
          (match) =>
            match.metadata?.type !== 'injected' && match.metadata?.origin !== 'search'
        )
        .map((match) => match.metadata.originalText);
      debugLog('🧠 Recalled vector memories (filtered):', recalledMemories);
    } catch (error) {
      console.error('❌ Error querying Pinecone:', error);
    }
  } else {
    debugLog('🧠 Skipping vector recall for low-signal message:', message);
  }

  if (useVectorMemory) {
    try {
      debugLog('🪵 Saving vector memory for:', message);
      await saveVectorMemory({ text: message, token });
    } catch (error) {
      console.error('❌ Failed to save vector memory:', error);
    }
  } else {
    debugLog('🪵 Skipping vector save for low-signal message:', message);
  }

  let mongoPayload;

  try {
    const searchResultsArray = Array.isArray(webSearchResult) ? webSearchResult : [];

    mongoPayload = prepareMemoryPayload({
      message,
      intent: intentInfo.intent,
      context,
      webSearchResults: searchResultsArray,
    });

    if (mongoPayload) {
      const memoryWriteExecutionCheck = validateExecution({
        intent: { type: 'memory_write' },
        targetEntity: null,
        relationshipState: currentRelationships,
        message,
        conversationState,
      });

      if (memoryWriteExecutionCheck.decision === 'BLOCK') {
        debugLog('🚫 [ExecutionBoundary] Mongo memory write blocked:', memoryWriteExecutionCheck.reason);
      } else if (memoryWriteExecutionCheck.decision === 'CONFIRM') {
        debugLog('⚠️ [ExecutionBoundary] Mongo memory write requires confirmation:', memoryWriteExecutionCheck.reason);
      } else {
        debugLog('📦 Mongo Payload:', mongoPayload);

        try {
          await axios.post('/api/memory/mongo/insert', mongoPayload);
        } catch (err) {
          console.error('❌ Failed to write to Mongo:', err.message);
        }

      }
    }
  } catch (err) {
    console.error('❌ Failed to prepare Mongo payload:', err.message);
  }

  try {
    const relationshipNetworkResult = intentInfo?.alreadyKnown === true
      ? {
          attempted: false,
          saved: [],
          existing: [],
          rejected: [],
          reason: 'Existing first-order relationship recognized; no graph mutation required.',
        }
      : await processRelationshipNetworkMemory({
          message,
          relationships: currentRelationships,
          relationshipNetwork: currentRelationshipNetwork,
          conversationState,
          intentInfo,
        });

    debugLog(
      `🕸️ [RelationshipNetwork] attempted=${relationshipNetworkResult?.attempted === true} saved=${relationshipNetworkResult?.saved?.length || 0} existing=${relationshipNetworkResult?.existing?.length || 0} rejected=${relationshipNetworkResult?.rejected?.length || 0} reason=${relationshipNetworkResult?.reason || 'none'}`
    );

    if (relationshipNetworkResult?.saved?.length) {
      if (typeof patchRelationshipNetworkEdges === 'function') {
        currentRelationshipNetwork = patchRelationshipNetworkEdges(
          relationshipNetworkResult.saved
        );
      }

      debugLog(
        '🕸️ [Chat] Relationship network memory saved:',
        relationshipNetworkResult
      );
    } else {
      debugLog(
        '🕸️ [Chat] Relationship network memory result:',
        relationshipNetworkResult
      );
    }
  } catch (err) {
    console.warn(
      '⚠️ [Chat] Relationship network memory processing failed:',
      err?.response?.data || err.message
    );
  }

  const promptMemory = buildPromptMemoryState({
    memoryState: {
      ...memory,
      userName: effectiveUserName,
    },
    conversationState,
  });

  const contextEnvelope = buildContextEnvelope({
    promptMemory,
    conversationState,
    intentInfo,
    detectedContext: {
      ...context,
      lastReferencedEntity: effectiveReferencedEntity,
      lastReferencedTopic: effectiveReferencedTopic,
    },
    recalledMemories,
    webSearchResult,
    activeIdentity,
    effectiveUserName,
    memoryName,
    relationships: currentRelationships,
    relationshipNetwork: currentRelationshipNetwork,
  });

  debugLog('🧠 Context Envelope:', contextEnvelope);
  debugLog('🧭 Conversation State:', conversationState);

  setMessages((prev) => [...prev, { text: message, sender: 'user' }]);

  try {
    setLoading(true);

    const aiResponse = await generateGovernedResponse(
      mongoPayload?.message || message,
      contextEnvelope,
      {
        ...intentInfo,
        memoryName,
        activeIdentity,
        knownRelationships: currentRelationships,
        lastReferencedEntity: effectiveReferencedEntity,
        lastReferencedTopic: effectiveReferencedTopic,
        frame: conversationState.frame || memory?.frame || null,
        userName: effectiveUserName,
        intentState,
        responseIntent: intentState?.responseIntent || null,
        objectiveDecisionState: conversationState.objectiveDecisionState || null,
        turnSignalArbitration: arbitration,
        pendingQuestionState: conversationState.pendingQuestionState || null,
        admissibilityState: conversationState.admissibilityState || null,
        activeFrameState: conversationState.activeFrameState || null,
        turnInterpretation,
        shapingPolicy,
      },
      {
        effectiveUserName,
        activeIdentity,
        relationships: currentRelationships,
        memoryFrame: memory?.frame ?? null,
        activeEntity: effectiveReferencedEntity,
        arbitration,
        onError: () => setLoading(false),
      }
    );

    const finalReply =
      typeof aiResponse === 'string'
        ? aiResponse
        : aiResponse?.response || '';

    if (typeof aiResponse === 'object' && aiResponse?.responsePolicyResult) {
      setLastResponsePolicyResult(aiResponse.responsePolicyResult);
    }

    setLoading(false);

    const nextPendingQuestion = commitPendingQuestionFromReply({
      botReply: finalReply,
      frame: conversationState.frame || memory?.frame || null,
      lastReferencedEntity: effectiveReferencedEntity,
      lastReferencedTopic: effectiveReferencedTopic,
      questionAuthority: shapingPolicy.questionAuthority,
    });

    setDebugConversationState((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        pendingQuestion: nextPendingQuestion,
        pendingQuestionState: {
          ...prev.pendingQuestionState,
          pendingQuestion: nextPendingQuestion,
          nextPendingQuestion,
          closePendingQuestion: false,
        },
      };
    });

    setMessages((prev) => [...prev, { text: finalReply, sender: 'bot' }]);
  } catch (err) {
    setLoading(false);
    console.error('❌ Failed to get chat response:', err.message);
  }
}
